import { CommonModule } from "@angular/common";
import { AfterViewInit, Component, ElementRef, EventEmitter, OnInit, Output, ViewChild } from "@angular/core";
import { IApiresponse } from "src/app/_models";
import { Claims } from "src/app/_models/common/claims";
import { ICountryCurrencyMappingResponse } from "src/app/_models/country-currency-mapping/country-currency-mapping";
import { IClaimantRangeMasterResponse } from "src/app/_models/claimant-range/claimant-range";
import { ClaimsService } from "src/app/_services/common/claims.service";
import { CountryCurrencyMappingService } from "src/app/_services/country-currency-mapping/country-currency-mapping.service";
import { ClaimantRangeService } from "src/app/_services/claimant-range/claimant-range.service";
import { TranslateModule } from "@ngx-translate/core";
import { FormsModule } from "@angular/forms";
import { TextboxComponent } from "../../../shared/design-system/textbox/textbox.component";
import { ButtonComponent } from "../../../shared/design-system/button/button.component";
import { NgSelectModule } from '@ng-select/ng-select';
import { TextboxConfig } from "../../../shared/design-system/textbox/textbox.modal";
import { TranslateService } from "@ngx-translate/core";
import { ButtonConfig } from "../../../shared/design-system/button/button.modal";
import { getCountryCallingCode, isValidPhoneNumber, CountryCode } from "libphonenumber-js";

@Component({
    selector: "app-organisation-details",
    templateUrl: "./organisation-details.component.html",
    standalone: true,
    imports: [CommonModule, TranslateModule, FormsModule, TextboxComponent, ButtonComponent, NgSelectModule]
})
export class OrganisationDetailsComponent implements OnInit, AfterViewInit {
  @Output() gotoNextStepEvent = new EventEmitter<any>();
  @ViewChild("orgNameField") orgNameField: ElementRef<HTMLInputElement>;

  orgNameConfig!: TextboxConfig;
  currencyConfig!: TextboxConfig;
  phoneConfig!: TextboxConfig;
  continueConfig!: ButtonConfig;
  claims: Claims;
  countryList: ICountryCurrencyMappingResponse[] = [];
  claimantRangeList: IClaimantRangeMasterResponse[] = [];
  orgName = "";
  assignedCountry: any;
  selectedCountryId: any;
  selectedClaimantRangeId: any;
  submitted = false;

  phoneNumber: string = "";
  dialCode: string = "";
  phoneInvalid = false;

  constructor(
    private claimsService: ClaimsService,
    private countryCurrencyMappingService: CountryCurrencyMappingService,
    private claimantRangeService: ClaimantRangeService,
    public translate: TranslateService
  ) {
    this.claimsService.currentClaims.subscribe(
      (claims) => (this.claims = claims)
    );
  }

  ngOnInit(): void {
    this.orgNameConfig = {
      id: 'organisation-name',
      label: this.translate.instant('organisation_name_label'),
      value: this.orgName,
      type: 'text',
      required: true,
      size: 'lg'
    };

    this.currencyConfig = {
      id: 'currency-input',
      required: true,
      label: this.translate.instant('currency'),
      value: this.assignedCountry?.currencyName,
      type: 'text',
      disabled: true,
      iconSrc: this.assignedCountry?.currencyIcon,
      iconPosition: 'left',
      size: 'lg',
      iconColor: 'color-remove'
    };
    this.phoneConfig = {
      id: 'phone-number',
      label: this.translate.instant('phone_number'),
      type: 'tel',
      required: true,
      size: 'lg',
      maxLength: 15,
    };

    this.getIpCliente();
    this.fnGetClaimantRangeMaster();

    this.setContinueButtonConfig();
  }

  setContinueButtonConfig() {
    this.continueConfig = {
      id: 'continue-button',
      label: this.translate.instant('continue'),
      color: "primary", 
      size: "lg",
      disabled: this.orgName.trim() === '' || !this.assignedCountry || this.selectedCountryId == 0 || !this.selectedClaimantRangeId || !this.phoneNumber || this.phoneInvalid,
      block: true,
      ariaLabel: this.translate.instant("continue")
    };
  }

  orgNameChange(val: string) {
    this.orgName = val;
    this.orgNameConfig = {
      ...this.orgNameConfig,
      value: val,
    };
    this.continueConfig = {
      ...this.continueConfig,
      disabled: this.orgName.trim() === '' || !this.assignedCountry || this.selectedCountryId == 0 || !this.selectedClaimantRangeId || !this.phoneNumber || this.phoneInvalid,
    };
  }

  onCountryChange() {
    this.assignedCountry = this.countryList.filter(
      (x) => x.countryId == this.selectedCountryId
    )[0];
    this.setDialCode();
    this.currencyConfig = {
      ...this.currencyConfig,
      value: this.assignedCountry?.currencyName,
      iconSrc: this.assignedCountry?.currencyIcon,
    };
    this.continueConfig = {
      ...this.continueConfig,
      disabled: this.orgName.trim() === '' || !this.assignedCountry || this.selectedCountryId == 0 || !this.selectedClaimantRangeId || !this.phoneNumber || this.phoneInvalid,
    };
  }

  onClaimantRangeChange() {
    this.continueConfig = {
      ...this.continueConfig,
      disabled: this.orgName.trim() === '' || !this.assignedCountry || this.selectedCountryId == 0 || !this.selectedClaimantRangeId || !this.phoneNumber || this.phoneInvalid,
    };
  }

  setDialCode() {
    const iso = this.assignedCountry?.countryCode as CountryCode;
    if (iso) {
      try {
        this.dialCode = getCountryCallingCode(iso);
      } catch {
        this.dialCode = "";
      }
    } else {
      this.dialCode = "";
    }
    this.phoneConfig = {
      ...this.phoneConfig,
      countryPhoneCode: this.dialCode ? '+' + this.dialCode : null,
    };
    this.validatePhone();
  }

  validatePhone() {
    const iso = this.assignedCountry?.countryCode as CountryCode;
    this.phoneInvalid =
      !!this.phoneNumber && !!iso && !isValidPhoneNumber(this.phoneNumber, iso);
  }

  onPhoneChange(val: string | number | null) {
    this.phoneNumber = (val ?? '') as string;
    this.validatePhone();
    this.phoneConfig = {
      ...this.phoneConfig,
      value: this.phoneNumber,
      isInvalid: this.phoneInvalid,
      helperText: this.phoneInvalid ? this.translate.instant('phone_number_is_invalid') : null,
    };
    this.continueConfig = {
      ...this.continueConfig,
      disabled: this.orgName.trim() === '' || !this.assignedCountry || this.selectedCountryId == 0 || !this.selectedClaimantRangeId || !this.phoneNumber || this.phoneInvalid,
    };
  }

  fnGetClaimantRangeMaster() {
    this.claimantRangeService
      .GetClaimantRangeMaster()
      .subscribe(
        (response: IApiresponse<IClaimantRangeMasterResponse[]>) => {
          if (response.responseInfo != null) {
            this.claimantRangeList = response.responseInfo;
          }
        }
      );
  }

  onSubmit() {
    this.submitted = true;
    if (!this.phoneNumber) {
      this.phoneConfig = {
        ...this.phoneConfig,
        isInvalid: true,
        helperText: this.translate.instant('phone_number_is_required'),
      };
    }
    if (
      this.orgName.trim() === "" ||
      !this.assignedCountry ||
      this.selectedCountryId == 0 ||
      !this.selectedClaimantRangeId ||
      !this.phoneNumber ||
      this.phoneInvalid
    ) {
      return;
    }
    this.claims.SelectedCountry = this.assignedCountry.countryId;
    this.claims.SelectedCountryName = this.assignedCountry.countryName;
    this.claims.SelectedCurrency = this.assignedCountry.currencyId;
    this.claims.SelectedCurrencyCode = this.assignedCountry.currencyCode;
    this.claims.SelectedCurrencyName = this.assignedCountry.currencyName;
    this.claims.CurrentOrganisationCurrency = this.assignedCountry.currencyCode;
    var currencyCode = this.claims.CurrentOrganisationCurrency;
    this.claims.CurrentOrganisationCurrencyCode = currencyCode;
    this.claims.OrgName = this.orgName.trim();
    this.claims.CurrencyCode = this.assignedCountry.currencyCode;
    this.claims.CurrencyIcon = this.assignedCountry.currencyIcon;
    this.claims.UserHomeCurrencyId = this.assignedCountry.currencyId;
    this.claims.UserHomeCurrencyCode = this.assignedCountry.currencyCode;
    this.claims.UserHomeCurrencyName = this.assignedCountry.currencyName;
    this.claims.ClaimantRangeMasterId = this.selectedClaimantRangeId;
    this.claims.ClaimantRangeLabel = this.claimantRangeList.find(c => c.claimantRangeMasterId == this.selectedClaimantRangeId)?.rangeLabel || ''; 
    this.claims.PhoneNumber = this.phoneNumber; 

    this.gotoNextStepEvent.emit(6);
  }

  gotoNextStep() {
    this.gotoNextStepEvent.emit(6);
  }

  async getIpCliente() {
    try {
      const response = await fetch(
        "https://api.ipapi.is/?key=02107353329c92f8"
      );
      const jsonData = await response.json();
      localStorage.setItem("countryName", jsonData.location.country);
      this.fnGetCountryCurrencyMapping();
    } catch (error) {
      this.fnGetCountryCurrencyMapping();
    }

  }

  fnGetCountryCurrencyMapping() {
    this.countryCurrencyMappingService
      .GetCountryCurrencyMapping()
      .subscribe(
        (response: IApiresponse<ICountryCurrencyMappingResponse[]>) => {
          if (response.responseInfo != null) {
            this.countryList = response.responseInfo;

            if (localStorage.getItem("countryName")) {
              var countryName = localStorage.getItem("countryName");

              let countryDetails = this.countryList.find(
                (c) => c.countryName == countryName
              );
              if (countryDetails) {
                this.assignedCountry = countryDetails;
                this.selectedCountryId = this.assignedCountry.countryId;
              } else {
                let defaultCurrencyDetails = this.countryList.find(
                  (c) => c.countryName == "United Kingdom"
                );
                if (defaultCurrencyDetails) {
                  this.assignedCountry = defaultCurrencyDetails;
                  this.selectedCountryId = this.assignedCountry.countryId;
                }
              }
            } else {
              let defaultCurrencyDetails = this.countryList.find(
                (c) => c.countryName == "United Kingdom"
              );
              if (defaultCurrencyDetails) {
                this.assignedCountry = defaultCurrencyDetails;
                this.selectedCountryId = this.assignedCountry.countryId;
              }
            }
          }          
          this.currencyConfig = {
            ...this.currencyConfig,
            value: this.assignedCountry?.currencyName,
            iconSrc: this.assignedCountry?.currencyIcon,
          };
          this.setDialCode();
        }
      );
  }

  ngAfterViewInit() {
    this.orgNameField.nativeElement.focus();
  }
}
